import {Grid, Typography, Box, Collapse} from '@material-ui/core';
import {SwitchCustom, TextBox, NumberFormatCustom} from '../../../Core/UI';
import React, {useState, useEffect, useMemo} from 'react';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import {Prequal} from '../../../Core/mobx/ParameterStore/Prequal';
import {Parameter} from '../../../Core/mobx/ParameterStore/ParameterStore';
import {observer} from 'mobx-react-lite';
import {CategoryCard} from '../CategoryCard';
import {Approval} from '../../../Core/mobx/ParameterStore/Approval';
import {SelectInputElement} from '../../PreQualification/Components/SelectInputElement';
import {fieldOptions} from '../../../Core/mobx/ParameterStore/ApprovalXref';
import ProductSelector from './ProductSelector';
import RootStore from '../../../Core/mobx/RootStore';

type PolicyInformationProps = {
    valueChanged: (p?: Parameter, value?: any) => void;
    parameters: Approval;
    setCategory: (entry: string) => void;
    index: number;
    setCompleted?: (completed: boolean) => void;
    reportMissingField?: (field: string | null) => void;
};

const PolicyInformation = observer(
    ({valueChanged, parameters, setCategory, index, setCompleted, reportMissingField}: PolicyInformationProps) => {
        const [secondaryExpanded, setSecondaryExpanded] = useState(false);

        const required = (value?: string) => (
            <span>
                {value && value} <span style={{color: 'red'}}>*</span>
            </span>
        );

        const {prequal, qualifiedProducts, recommendedProductsResponse} = RootStore.agentPortalStore.parameterStore;

        // Get all products to map between codes and names
        const allProducts = useMemo(() => {
            const products =
                recommendedProductsResponse?.allProducts ||
                recommendedProductsResponse?.products ||
                qualifiedProducts ||
                [];
            // console.log('PolicyInformation allProducts:', {
            //     count: products.length,
            //     first5: products.slice(0, 5).map((p: any) => ({code: p.code, name: p.name})),
            //     hasRecommendedResponse: !!recommendedProductsResponse,
            //     hasQualifiedProducts: !!qualifiedProducts,
            // });
            return products;
        }, [recommendedProductsResponse, qualifiedProducts]);

        // Get the recommended product code directly from the approval store (copied from prequal)
        const recommendedProductCode = useMemo(() => {
            const value = parameters.recommendedProductCode?.value || '';
            // console.log('PolicyInformation recommendedProductCode:', {
            //     value,
            //     parametersRecommendedProductCode: parameters.recommendedProductCode?.value,
            // });
            return value;
        }, [parameters.recommendedProductCode?.value]);

        // Log initial state when component mounts/updates
        // useEffect(() => {
        //     console.log('PolicyInformation mount/update - Current values:', {
        //         firstCarrierQuoted: parameters.firstCarrierQuoted?.value,
        //         recommendedProductCode: parameters.recommendedProductCode?.value,
        //         agentProducts: parameters.agentProducts?.value,
        //         agentProductCodes: parameters.agentProductCodes?.value,
        //         prequal: {
        //             selectedProduct: prequal?.selectedProduct?.value,
        //             selectedProductCode: prequal?.selectedProductCode?.value,
        //             recommendedProductCode: prequal?.recommendedProductCode?.value,
        //         },
        //     });
        // }, [
        //     parameters.firstCarrierQuoted?.value,
        //     parameters.recommendedProductCode?.value,
        //     parameters.agentProducts?.value,
        //     parameters.agentProductCodes?.value,
        // ]);

        useEffect(() => {
            const completed =
                parameters.firstCarrierQuoted?.value !== '' &&
                parameters.firstProductType?.value !== '' &&
                parameters.firstCoverageAmount?.value !== '' &&
                parameters.firstMonthlyPremium?.value !== '' &&
                parameters.firstHealthRating?.value !== '';
            setCompleted?.(completed);

            let missing: string | null = null;
            if (parameters.firstCarrierQuoted?.value === '') missing = 'First Carrier Quoted';
            else if (parameters.firstProductType?.value === '') missing = 'First Product Type Quoted';
            else if (parameters.firstCoverageAmount?.value === '') missing = 'First Coverage Amount Quoted';
            else if (parameters.firstMonthlyPremium?.value === '') missing = '1st Carrier Mo. Tgt. Prem.';
            else if (parameters.firstHealthRating?.value === '') missing = 'First Rate Class Quoted';
            reportMissingField?.(missing);
        }, [
            setCompleted,
            reportMissingField,
            parameters.firstCarrierQuoted?.value,
            parameters.firstProductType?.value,
            parameters.firstCoverageAmount?.value,
            parameters.firstMonthlyPremium?.value,
            parameters.firstHealthRating?.value,
        ]);

        return (
            <CategoryCard title={'Policy Information'} setCategory={setCategory} index={index}>
                <Grid item xs={4}>
                    <ProductSelector
                        param={{
                            label: 'First Carrier Quoted',
                            name: 'firstCarrierQuoted',
                            value: parameters.firstCarrierQuoted?.value,
                            setValue: (v: any) => {
                                // console.log('PolicyInformation: Setting firstCarrierQuoted to:', v);
                                parameters.firstCarrierQuoted?.setValue?.(v);
                            },
                        }}
                        required={required('')}
                        valueChanged={valueChanged}
                    />
                </Grid>
                <Grid item xs={4}>
                    <SelectInputElement
                        param={{
                            label: 'First Product Type Quoted',
                            name: 'firstProductType',
                            options: fieldOptions.firstProductType.options,
                            ...parameters.firstProductType,
                        }}
                        required={required('')}
                        valueChanged={valueChanged}
                    />
                </Grid>
                <Grid item xs={4}>
                    <TextBox
                        id="firstCoverageAmount"
                        label={required('First Coverage Amount Quoted')}
                        value={parameters.firstCoverageAmount?.value}
                        onChange={(value) => valueChanged(parameters.firstCoverageAmount, value)}
                        InputProps={{
                            inputComponent: NumberFormatCustom,
                        }}
                    />
                </Grid>
                <Grid item xs={4}>
                    <TextBox
                        id="firstMonthlyPremium"
                        label={required('1st Carrier Mo. Tgt. Prem.')}
                        value={parameters.firstMonthlyPremium?.value}
                        onChange={(value) => valueChanged(parameters.firstMonthlyPremium, value)}
                        InputProps={{
                            inputComponent: NumberFormatCustom,
                        }}
                    />
                </Grid>
                <Grid item xs={4}>
                    <SelectInputElement
                        param={{
                            label: 'First Rate Class Quoted',
                            name: 'firstHealthRating',
                            options: fieldOptions.firstHealthRating.options,
                            ...parameters.firstHealthRating,
                        }}
                        required={required('')}
                        valueChanged={valueChanged}
                    />
                </Grid>
                <Grid item xs={4}>
                    <TextBox
                        id="firstFreeResponse"
                        label={'First Reason'}
                        value={parameters.firstFreeResponse?.value}
                        onChange={(value) => valueChanged(parameters.firstFreeResponse, value)}
                    />
                </Grid>
                <Grid item xs={12}>
                    <Box
                        onClick={() => setSecondaryExpanded(!secondaryExpanded)}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            cursor: 'pointer',
                            paddingTop: 8,
                            paddingBottom: 8,
                        }}
                    >
                        <Typography variant="overline" style={{fontSize: 16}}>
                            Secondary policy or reapply info
                        </Typography>
                        <ExpandMoreIcon
                            style={{
                                transform: secondaryExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                transition: 'transform 0.2s',
                                marginLeft: 8,
                            }}
                        />
                    </Box>
                </Grid>
                <Collapse in={secondaryExpanded} timeout="auto" unmountOnExit style={{width: '100%'}}>
                    <Grid container spacing={2}>
                        <Grid item xs={4}>
                            <ProductSelector
                                param={{
                                    label: 'Second Carrier Quoted',
                                    name: 'secondCarrierQuoted',
                                    value: parameters.secondCarrierQuoted?.value,
                                    setValue: (v: any) => parameters.secondCarrierQuoted?.setValue?.(v),
                                }}
                                required={''}
                                valueChanged={valueChanged}
                            />
                        </Grid>
                        <Grid item xs={4}>
                            <SelectInputElement
                                param={{
                                    label: 'Second Product Type Quoted',
                                    name: 'secondProductType',
                                    options: fieldOptions.secondProductType.options,
                                    ...parameters.secondProductType,
                                }}
                                required={''}
                                valueChanged={valueChanged}
                            />
                        </Grid>
                        <Grid item xs={4}>
                            <TextBox
                                id="secondCoverageAmount"
                                label={'Second Coverage Amount Quoted'}
                                value={parameters.secondCoverageAmount?.value}
                                onChange={(value) => valueChanged(parameters.secondCoverageAmount, value)}
                                InputProps={{
                                    inputComponent: NumberFormatCustom,
                                }}
                            />
                        </Grid>
                        <Grid item xs={4}>
                            <TextBox
                                id="secondMonthlyPremium"
                                label={'2nd Carrier Mo. Tgt. Prem.'}
                                value={parameters.secondMonthlyPremium?.value}
                                onChange={(value) => valueChanged(parameters.secondMonthlyPremium, value)}
                                InputProps={{
                                    inputComponent: NumberFormatCustom,
                                }}
                            />
                        </Grid>
                        <Grid item xs={4}>
                            <SelectInputElement
                                param={{
                                    label: 'Second Rate Class Quoted',
                                    name: 'secondHealthRating',
                                    options: fieldOptions.secondHealthRating.options,
                                    ...parameters.secondHealthRating,
                                }}
                                required={''}
                                valueChanged={valueChanged}
                            />
                        </Grid>
                        <Grid item xs={4}>
                            <TextBox
                                id="secondFreeResponse"
                                label={'Second Reason'}
                                value={parameters.secondFreeResponse?.value}
                                onChange={(value) => valueChanged(parameters.secondFreeResponse, value)}
                            />
                        </Grid>
                        <Grid item xs={4}>
                            <ProductSelector
                                param={{
                                    label: 'Reapply Carrier Quoted',
                                    name: 'reapplyCarrierQuoted',
                                    value: parameters.reapplyCarrierQuoted?.value,
                                    setValue: (v: any) => parameters.reapplyCarrierQuoted?.setValue?.(v),
                                }}
                                required={''}
                                valueChanged={valueChanged}
                            />
                        </Grid>
                        <Grid item xs={4}>
                            <SelectInputElement
                                param={{
                                    label: 'Reapply Product Type Quoted',
                                    name: 'reapplyProductType',
                                    options: fieldOptions.reapplyProductType.options,
                                    ...parameters.reapplyProductType,
                                }}
                                required={''}
                                valueChanged={valueChanged}
                            />
                        </Grid>
                        <Grid item xs={4}>
                            <TextBox
                                id="reapplyCoverageAmount"
                                label={'Reapply Coverage Amount Quoted'}
                                value={parameters.reapplyCoverageAmount?.value}
                                onChange={(value) => valueChanged(parameters.reapplyCoverageAmount, value)}
                                InputProps={{
                                    inputComponent: NumberFormatCustom,
                                }}
                            />
                        </Grid>
                        <Grid item xs={4}>
                            <TextBox
                                id="reapplyMonthlyPremium"
                                label={'Reapply Mo. Target Premium Quoted'}
                                value={parameters.reapplyMonthlyPremium?.value}
                                onChange={(value) => valueChanged(parameters.reapplyMonthlyPremium, value)}
                                InputProps={{
                                    inputComponent: NumberFormatCustom,
                                }}
                            />
                        </Grid>
                        <Grid item xs={4}>
                            <SelectInputElement
                                param={{
                                    label: 'Reapply Rate Class Quoted',
                                    name: 'reapplyHealthRating',
                                    options: fieldOptions.reapplyHealthRating.options,
                                    ...parameters.reapplyHealthRating,
                                }}
                                required={''}
                                valueChanged={valueChanged}
                            />
                        </Grid>
                        {parameters.state.value === 'NY' && (
                            <Grid item xs={4}>
                                <SwitchCustom
                                    param={{
                                        name: 'nyIllustrationSigned',
                                        label: 'NY Illustration Signed',
                                        ...parameters.nyIllustrationSigned,
                                    }}
                                    required={required('')}
                                    valueChanged={valueChanged}
                                />
                            </Grid>
                        )}
                    </Grid>
                </Collapse>
            </CategoryCard>
        );
    },
);

export default PolicyInformation;
